/* eslint-disable react-hooks/exhaustive-deps */

import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { fetchAccounts, selectAccounts } from '@/features/accounts/accountsSlice';
import { fetchCBSStatus, fetchSettings, fetchUser, selectUsers } from '@/features/user/userSlice';
import { fetchDetails, fetchExchangeRates } from '@/features/data/dataSlice';
import { loginRoutes, withFullAuthEndpoins, withTmpAuthEndpoints, withoutAuthEndpoints } from '@/utils/routsConst';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useTermsAndConditionsCheck } from '@/hooks/useTermsAndConditionsCheck';

import CbsNotification from '@/components/CbsNotification';
import Cookies from 'js-cookie';
import { Dashboard } from '@mui/icons-material';
import HeadAlert from '@/components/HeadAlert';
import { Loading } from '@/pages/LoadingPage';
import clsx from 'clsx';
import { signOutScope } from '@/utils/session';
import { useFirstRender } from '@/hooks/useFirstRender';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import { fetchPublicAuthUserSettings } from '@/features/publicSettings/publicSettingsSlice';
import { useSubscriptionNotification } from '@/hooks/useSubscriptionNotification';
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const CreateNewPasswordPage = lazy(() => import('@/pages/Login/CreateNewPasswordPage'));
const SuccessLoginPage = lazy(() => import('@/pages/Login/SuccessLoginPage'));
const TransactionsPage = lazy(() => import('@/pages/TransactionsPage'));
const InternalTransfer = lazy(() => import('@/pages/Transfer/Internal'));
const InternationalTransfer = lazy(() => import('@/pages/Transfer/International'));
const MyStatementsPage = lazy(() => import('@/pages/MyStatements'));
const PaymentPage = lazy(() => import('@/pages/Payment'));
const NewPaymentPage = lazy(() => import('@/pages/NewPayment'));
const NewNraPayment = lazy(() => import('@/pages/Payment/NraPayment'));
const MyAccountsPage = lazy(() => import('@/pages/Accounts'));
const UserProfilePage = lazy(() => import('@/pages/UserProfilePage'));
const PhoneNumberValidationPage = lazy(() => import('@/pages/Login/PhoneNumberValidationPage'));
const CodeVerificationPage = lazy(() => import('@/pages/Login/CodeVerificationPage'));
const PhoneVerificationPage = lazy(() => import('@/pages/ForgotPassword/PhoneVerificationPage'));
const LoginPage = lazy(() => import('@/pages/Login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword/ForgotPasswordPage'));
const LoansPage = lazy(() => import('@/pages/LoansPage'));
const MoneyTransfersPage = lazy(() => import('@/pages/MoneyTransfersPage'));
const FaqPage = lazy(() => import('@/pages/Faq/AllGroups'));
const ExchangesPage = lazy(() => import('@/pages/ExchangePage'));
const SubUserTransactionsPage = lazy(() => import('@/pages/SubUserTransactions'));
const TransactionsValidationPage = lazy(() => import('@/pages/TransactionsValidation'));
const TransactionsPendingPage = lazy(() => import('@/pages/TransactionsPending'));
const ManageSubUser = lazy(() => import('@/components/UserProfile/UsersManagement/ManageSubUser'));
const CardsPage = lazy(() => import('@/pages/Cards'));
const AboutUsPage = lazy(() => import('@/pages/AboutUs'));
const AlertsPage = lazy(() => import('@/pages/Alerts'));
const DepositPage = lazy(() => import('@/pages/Deposit'));
const SavingPage = lazy(() => import('@/pages/Saving'));
const TicketsManagementPage = lazy(() => import('@/pages/TicketsManagement'));
const CheckbookManagementPage = lazy(() => import('@/pages/CheckbookManagement'));
const TOTPSetupPage = lazy(() => import('@/pages/TOTP'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const PrepaidRechargePage = lazy(() => import('@/pages/PrepaidRecharge'));
const PrepaidRechargeSummaryPage = lazy(() => import('@/pages/PrepaidRechargeSummary'));

export const PageNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser, cbsStatus, billingError } = useAppSelector(selectUsers);
  const { loading, value: accounts } = useAppSelector(selectAccounts);

  const dispatch = useAppDispatch();
  const { checkTermsAndConditions, recheckTermsAndConditionsStatus } = useTermsAndConditionsCheck();
  const { isStatementsFeature, isCheckbookFeature } = useFeatureFlags();
  const [renderPage, setRenderPage] = useState(false);
  const dataLoadStarted = useRef(false);

  useEffect(() => {
    if (!currentUser.id) dataLoadStarted.current = false;
  }, [currentUser.id]);

  const firstRender = useFirstRender();
  const {
    showNotification,
    endDayDiff,
    tier: subscriptionTier,
  } = useSubscriptionNotification(currentUser.duePaymentDate, currentUser.subscriptionInfo);

  useEffect(() => {
    document.getElementById('app')?.scrollTo(0, 0);

    if (Cookies.get('accessToken') && localStorage.getItem('needChangePass')) {
      navigate('/create-new-password', { state: { forgotPasswordFlow: false } });
      return setRenderPage(true);
    }
    if (Cookies.get('accessToken') && location.pathname === '/') return navigate('/dashboard');

    if (
      Cookies.get('accessToken') &&
      (loginRoutes.includes(location.pathname) || withoutAuthEndpoints.includes(location.pathname)) &&
      !withFullAuthEndpoins.includes(location.pathname)
    )
      return navigate('/dashboard');
    // IB1-306 (épique #5428, F1.1 Variante C, F5.1 RM-01) : accès à une route protégée (dont
    // /checkbook) sans session active — redirection avec message dédié plutôt qu'un login muet.
    if (isValidParams()) return navigate('/log-in', { state: { error: t('errors.session_expired') } });
    if (
      Cookies.get('tmpToken') &&
      !withTmpAuthEndpoints.includes(location.pathname) &&
      !withTmpAuthEndpoints.some(route => location.pathname.startsWith(route))
    )
      return navigate('/log-in');

    setRenderPage(true);
    if (['/create-new-password', '/success-login', '/totp-setup'].includes(location.pathname)) return;

    Cookies.get('accessToken') && !currentUser.id && fetchData();
  }, [location.pathname, currentUser]);

  const isValidParams = () => {
    const isExactTmpRoute = withTmpAuthEndpoints.includes(location.pathname);
    const isPartialTmpRoute = withTmpAuthEndpoints.some(route => location.pathname.startsWith(route) && location.pathname !== route);

    return (
      !Cookies.get('accessToken') &&
      !Cookies.get('tmpToken') &&
      !withoutAuthEndpoints.includes(location.pathname) &&
      (withFullAuthEndpoins.includes(location.pathname) || (isExactTmpRoute && !isPartialTmpRoute))
    );
  };

  const handleLogout = () => {
    Cookies.get('accessToken') &&
      signOutScope()
        .then((_resp: any) => navigate('/log-in', { state: { error: t('dashboard.notAccounts') } }))
        .catch(err => console.log(err.data.message));
  };

  useEffect(() => {
    if (firstRender) return;
    if (accounts.length === 0 && !loading) return handleLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, loading]);

  const loadAuthenticatedData = () => {
    if (dataLoadStarted.current) return;
    dataLoadStarted.current = true;
    dispatch(fetchUser());
    dispatch(fetchExchangeRates());
    dispatch(fetchDetails());
    dispatch(fetchCBSStatus());
    dispatch(fetchAccounts());
    dispatch(fetchSettings());
    dispatch(fetchPublicAuthUserSettings());
  };

  const fetchData = () => {
    checkTermsAndConditions();
    loadAuthenticatedData();
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Cookies.get('accessToken')) {
        recheckTermsAndConditionsStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [recheckTermsAndConditionsStatus]);
  const clienSideRouter = () => {
    return (
      <Routes>
        <Route path='/' element={<Dashboard />} />
        <Route path='/dashboard' element={<DashboardPage />} />
        <Route path='/create-new-password' element={<CreateNewPasswordPage />} />
        <Route path='/success-login' element={<SuccessLoginPage />} />
        <Route path='/transactions' element={<TransactionsPage />} />
        <Route path='/my-statements' element={isStatementsFeature ? <MyStatementsPage /> : <NotFoundPage />} />
        <Route path='/money-transfers/internal' element={<InternalTransfer />} />
        <Route path='/money-transfers/international' element={<InternationalTransfer />} />
        <Route path='/payments' element={<PaymentPage />} />
        <Route path='/payments/set-standing-order' element={<NewPaymentPage />} />
        <Route path='/payments/new-payment' element={<NewPaymentPage />} />
        <Route path='/payments/new-nra-payment' element={<NewNraPayment />} />
        <Route path='/my-accounts' element={<MyAccountsPage />} />
        <Route path='/profile' element={<UserProfilePage />} />
        <Route path='/phone-number-validation' element={<PhoneNumberValidationPage />} />
        <Route path='/code-verification' element={<CodeVerificationPage />} />
        <Route path='/phone-verification' element={<PhoneVerificationPage />} />
        <Route path='/log-in' element={<LoginPage />} />
        <Route path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route path='/loans' element={<LoansPage />} />
        <Route path='/money-transfers' element={<MoneyTransfersPage />} />
        <Route path='/faq' element={<FaqPage />} />
        <Route path='/exchanges' element={<ExchangesPage />} />
        <Route path='/sub-user-transactions' element={<SubUserTransactionsPage />} />
        <Route path='/transactions-validation' element={<TransactionsValidationPage />} />
        <Route path='/transactions-pending' element={<TransactionsPendingPage />} />
        <Route path='/profile/edit-sub-user' element={<ManageSubUser />} />
        <Route path='/profile/create-sub-user' element={<ManageSubUser />} />
        <Route path='/cards' element={<CardsPage />} />
        <Route path='/cards/prepaid-recharge' element={<PrepaidRechargePage />} />
        <Route path='/cards/prepaid-recharge/summary' element={<PrepaidRechargeSummaryPage />} />
        <Route path='/deposits' element={<DepositPage />} />
        <Route path='/about-us' element={<AboutUsPage />} />
        <Route path='/alerts' element={<AlertsPage />} />
        <Route path='/saving' element={<SavingPage />} />
        <Route path='/tickets' element={<TicketsManagementPage />} />
        <Route path='/checkbook' element={isCheckbookFeature ? <CheckbookManagementPage /> : <NotFoundPage />} />
        <Route path='/totp-setup' element={<TOTPSetupPage />} />
        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    );
  };

  return (
    <main className={clsx(loginRoutes.includes(location.pathname) || !Cookies.get('accessToken') ? 'main-secondary' : 'main')} id='main'>
      {showNotification && subscriptionTier && <HeadAlert endDays={endDayDiff} tier={subscriptionTier} />}
      {(cbsStatus !== 'UP' || billingError === 'bill_not_found') && <CbsNotification />}
      {renderPage && <Suspense fallback={<Loading />}>{clienSideRouter()}</Suspense>}
    </main>
  );
};
